<script setup lang="ts">
import { useClassStudents } from '@/composables/student/useClassStudents';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import GroupIcon from '@/assets/icons/GroupIcon.vue';
import { useStudentSelectionLabel } from '@/composables/useStudentSelectionLabel';
import UserAvatarGroup from '@/core/elements/UserAvatarGroup.vue';
import StudentSelectionPopover from '@/core/selects/StudentSelectionPopover.vue';
import { EObservationTone, type TStudentLike } from '@/interfaces';
import { resolveStudentSelection } from '@/utils/students.utils';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

defineOptions({ name: 'StudentSelection', inheritAttrs: false });

const emit = defineEmits<{
  'select:all-eligible-students': [];
  'update:students': [students: TStudentLike[]];
  show: [anchor: HTMLElement];
  hide: [];
}>();

const props = withDefaults(
  defineProps<{
    disableClickOutside?: boolean;
    disabled?: boolean;
    /** Batch edits and emit `update:students` once when the popover closes, instead of per toggle. */
    commitOnClose?: boolean;
    /** Students available in the current subject scope. */
    eligibleStudents: TStudentLike[];
    /** Compact selection state; the chip derives whether this is literally the whole class. */
    allEligibleStudentsSelected: boolean;
    maxNamedStudents?: number;
    mobileDrawer?: boolean;
    readOnly?: boolean;
    selectedStudents: TStudentLike[];
    tone?: EObservationTone;
  }>(),
  {
    disableClickOutside: false,
    disabled: false,
    commitOnClose: false,
    maxNamedStudents: 3,
    mobileDrawer: false,
    readOnly: false,
    tone: EObservationTone.Default,
  },
);

const { t } = useI18n();
const { currentClass } = useCurrentClass();
const { allStudents } = useClassStudents(() => currentClass.value?.id);
const buttonRef = useTemplateRef<HTMLElement>('buttonRef');
const popoverRef = useTemplateRef('popoverRef');
const popoverOpen = ref(false);

const representedStudents = computed(() =>
  resolveStudentSelection({
    allEligibleStudentsSelected: props.allEligibleStudentsSelected,
    eligibleStudents: props.eligibleStudents,
    selectedStudents: props.selectedStudents,
  }),
);

const { label, wholeClassSelected } = useStudentSelectionLabel(representedStudents, allStudents, () => {
  if (!representedStudents.value.length) return t('students.none');
  if (representedStudents.value.length > props.maxNamedStudents) {
    return t('students.count', { count: representedStudents.value.length });
  }
  return representedStudents.value.map((student) => student.firstName).join(', ');
});

const showAvatars = computed(() => !wholeClassSelected.value && representedStudents.value.length > 0);

const ariaLabel = computed(() => `${t('students.select')}: ${label.value}`);

const show = async (): Promise<void> => {
  if (props.disabled || props.readOnly || popoverOpen.value || !buttonRef.value) return;

  emit('show', buttonRef.value);
  popoverOpen.value = true;
  await popoverRef.value?.show();
};

const hide = (): void => {
  if (!popoverOpen.value) return;
  popoverRef.value?.hide();
};

const togglePopover = (): void => {
  if (popoverOpen.value) hide();
  else void show();
};

const handlePopoverHide = (): void => {
  popoverOpen.value = false;
  emit('hide');
};

defineExpose({ element: buttonRef, show, hide, isOpen: computed(() => popoverOpen.value) });
</script>

<template>
  <component
    :is="props.readOnly ? 'span' : 'button'"
    ref="buttonRef"
    v-bind="$attrs"
    :type="props.readOnly ? undefined : 'button'"
    class="chip text-primary"
    :class="[
      props.tone === EObservationTone.Note ? 'bg-surface-note-strong note-state-effects' : 'bg-surface-tertiary',
      showAvatars && representedStudents.length > 1 && '!pl-sm',
    ]"
    :disabled="props.readOnly ? undefined : props.disabled"
    :aria-haspopup="props.readOnly ? undefined : 'dialog'"
    :aria-expanded="props.readOnly ? undefined : popoverOpen"
    :aria-label="props.readOnly ? undefined : ariaLabel"
    @click="togglePopover"
  >
    <GroupIcon v-if="!showAvatars" class="size-3 shrink-0" />
    <UserAvatarGroup v-else :users="representedStudents" :max="props.maxNamedStudents" size="sm" />
    <span class="max-w-52 truncate">{{ label }}</span>
    <ChevronDownIcon v-if="!props.readOnly" class="-ml-sm size-icon-xs shrink-0 text-secondary" aria-hidden="true" />
  </component>

  <StudentSelectionPopover
    v-if="!props.readOnly"
    ref="popoverRef"
    :anchor-element="buttonRef"
    :all-eligible-students-selected="props.allEligibleStudentsSelected"
    :commit-on-close="props.commitOnClose"
    :disable-click-outside="props.disableClickOutside"
    :eligible-students="props.eligibleStudents"
    :mobile-drawer="props.mobileDrawer"
    :selected-students="props.selectedStudents"
    @select:all-eligible-students="emit('select:all-eligible-students')"
    @update:students="emit('update:students', $event)"
    @hide="handlePopoverHide"
  />
</template>
